/**
 * Google Maps Places API を使用した駅自動補完入力フィールド
 * FL-270: 陸送員マスターデータに最寄駅情報を追加
 *
 * 責務:
 * - Google Maps Places Autocomplete API との連携（駅検索）
 * - リアルタイム駅補完候補の提示
 * - ユーザー選択時の駅詳細情報取得トリガー
 * - 入力値の変更検知と親コンポーネントへの通知
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { googleMapsService } from '@/services/maps/googleMapsService';
import type { StationResult, StationDetails } from '@/types/maps';

export interface StationAutocompleteInputProps {
  /** 入力フィールドのid属性（アクセシビリティ） */
  id?: string;

  /** ラベルテキスト */
  label?: string;

  /** プレースホルダーテキスト */
  placeholder?: string;

  /** 現在の入力値 */
  value: string;

  /** 入力値変更時のコールバック */
  onChange: (value: string) => void;

  /** 駅選択時のコールバック（駅詳細情報取得トリガー）
   * @param stationName 選択された駅名
   * @param stationDetails 駅の詳細情報（座標、住所など）
   */
  onStationSelect: (stationName: string, stationDetails: StationDetails) => void;

  /** 必須フィールドか */
  required?: boolean;

  /** エラーメッセージ */
  error?: string;

  /** 無効化状態 */
  disabled?: boolean;

  /** クラス名（Tailwind対応） */
  className?: string;
}

/**
 * Google Maps Places Autocomplete APIを使用した駅検索コンポーネント
 *
 * 機能:
 * - 2文字以上入力時にリアルタイム駅補完を提示
 * - ドロップダウンからの選択で onStationSelect を発火（駅詳細情報付き）
 * - 入力値の即座の通知で formData を更新
 * - エラーハンドリングとロード状態表示
 * - API失敗時は空配列を返す（フォールバック対応）
 *
 * @example
 * <StationAutocompleteInput
 *   label="最寄駅"
 *   placeholder="駅名を入力してください..."
 *   value={formData.nearestStation}
 *   onChange={(station) => setFormData({...formData, nearestStation: station})}
 *   onStationSelect={(name, details) => {
 *     setFormData({
 *       ...formData,
 *       nearestStation: name,
 *       nearestStationLat: details.latitude,
 *       nearestStationLng: details.longitude,
 *     });
 *   }}
 *   required
 * />
 */
export const StationAutocompleteInput = React.forwardRef<
  HTMLInputElement,
  StationAutocompleteInputProps
>(
  (
    {
      id,
      label,
      placeholder = '駅名を入力してください',
      value,
      onChange,
      onStationSelect,
      required = false,
      error,
      disabled = false,
      className = '',
    },
    ref
  ) => {
    // ドロップダウン表示制御
    const [isOpen, setIsOpen] = useState(false);

    // 補完候補リスト
    const [suggestions, setSuggestions] = useState<StationResult[]>([]);

    // ロード状態
    const [isLoading, setIsLoading] = useState(false);

    // エラー状態
    const [suggestionsError, setSuggestionsError] = useState<string>();

    // 選択されたサジェストインデックス（キーボード操作用）
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // ドロップダウンコンテナへの参照（click outside検知用）
    const containerRef = useRef<HTMLDivElement>(null);

    // デバウンス用の ref（Google Maps API 呼び出し削減）
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // デバッグ：disabled 状態の監視
    useEffect(() => {
      console.log('[StationAutocompleteInput] disabled 状態変更:', {
        disabled,
        label,
        id,
        value,
      });
    }, [disabled, label, id, value]);

    /**
     * Google Maps Places Autocomplete API を呼び出し、駅補完候補を取得
     */
    const fetchSuggestions = useCallback(async (input: string) => {
      // 入力が2文字未満の場合はスキップ
      if (input.trim().length < 2) {
        setSuggestions([]);
        setSelectedIndex(-1);
        return;
      }

      setIsLoading(true);
      setSuggestionsError(undefined);

      try {
        const results = await googleMapsService.searchNearestStation(input);
        setSuggestions(results);
        setSelectedIndex(-1); // リセット

        // API が空配列を返した場合（フォールバック）
        if (results.length === 0 && input.trim().length >= 2) {
          console.warn('[StationAutocompleteInput] 駅の検索結果なし（フォールバック）');
        }
      } catch (err) {
        console.error('[StationAutocompleteInput] 駅補完取得エラー:', err);
        setSuggestionsError(
          err instanceof Error
            ? err.message
            : '駅補完の取得に失敗しました（手動入力でご利用ください）'
        );
        // エラーでも駅検索を中止せず、ユーザーが手動入力できるようにする
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    /**
     * デバウンスされた fetchSuggestions（300ms）
     * Google Maps API の使用量を削減するため、ユーザーの入力が落ち着くまで待機
     */
    const debouncedFetchSuggestions = useCallback(
      (input: string) => {
        // 既存のタイマーをクリア
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        // 新しいタイマーを設定（300ms後に実行）
        debounceTimerRef.current = setTimeout(() => {
          fetchSuggestions(input);
        }, 300);
      },
      [fetchSuggestions]
    );

    /**
     * 入力値変更時のハンドラー
     */
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value;

        // デバッグ：入力イベント発火確認
        console.log('[StationAutocompleteInput] 入力変更検出:', {
          newValue,
          length: newValue.length,
          disabled,
          isLoading: isLoading,
          isOpen,
        });

        // 親コンポーネントに値を通知（formData更新）
        onChange(newValue);

        // ドロップダウンを開く（2文字以上）
        setIsOpen(newValue.trim().length >= 2);

        // デバウンスされた補完候補取得（300ms）
        debouncedFetchSuggestions(newValue);
      },
      [onChange, debouncedFetchSuggestions, disabled, isLoading, isOpen]
    );

    /**
     * サジェスト選択時のハンドラー（駅詳細情報を取得）
     */
    const handleSuggestionSelect = useCallback(
      async (suggestion: StationResult) => {
        console.log('[StationAutocompleteInput] 駅選択:', {
          name: suggestion.name,
          placeId: suggestion.placeId,
        });

        // 入力フィールドに駅名を設定
        onChange(suggestion.name);

        // ロード中フラグを設定
        setIsLoading(true);

        try {
          // 駅の詳細情報を取得（座標・住所）
          const stationDetails = await googleMapsService.getStationDetails(suggestion.placeId);

          if (stationDetails.status === 'OK') {
            // 親コンポーネントに選択を通知（駅詳細情報付き）
            onStationSelect(suggestion.name, stationDetails);
          } else {
            // エラー時も親に通知（ユーザーが手動で詳細情報を入力する可能性）
            console.warn('[StationAutocompleteInput] 駅詳細情報取得失敗:', {
              status: stationDetails.status,
              error: stationDetails.error_message,
            });
            // 駅名だけは通知する（位置情報はオプション）
            const fallbackDetails: StationDetails = {
              name: suggestion.name,
              placeId: suggestion.placeId,
              latitude: 0,
              longitude: 0,
              formattedAddress: '',
              status: stationDetails.status,
              error_message: stationDetails.error_message,
            };
            onStationSelect(suggestion.name, fallbackDetails);
          }
        } catch (err) {
          console.error('[StationAutocompleteInput] 駅詳細情報取得エラー:', err);
          // エラーでも駅名は利用可能にする（フォールバック）
          const fallbackDetails: StationDetails = {
            name: suggestion.name,
            placeId: suggestion.placeId,
            latitude: 0,
            longitude: 0,
            formattedAddress: '',
            status: 'UNKNOWN_ERROR',
            error_message: err instanceof Error ? err.message : 'Unknown error',
          };
          onStationSelect(suggestion.name, fallbackDetails);
        } finally {
          setIsLoading(false);
          // ドロップダウンを閉じる
          setIsOpen(false);
          setSuggestions([]);
          setSelectedIndex(-1);
        }
      },
      [onChange, onStationSelect]
    );

    /**
     * キーボード操作（矢印キー、Enter）ハンドラー
     */
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || suggestions.length === 0) {
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
            break;

          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
            break;

          case 'Enter':
            e.preventDefault();
            if (selectedIndex >= 0) {
              handleSuggestionSelect(suggestions[selectedIndex]);
            }
            break;

          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            break;

          default:
            break;
        }
      },
      [isOpen, suggestions, selectedIndex, handleSuggestionSelect]
    );

    /**
     * 外側クリック検知（ドロップダウン閉じる）
     */
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
      return undefined;
    }, [isOpen]);

    return (
      <div ref={containerRef} className={`relative w-full ${className}`}>
        {/* ラベル */}
        {label && (
          <label
            htmlFor={id}
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            {label}
            {required && <span className='text-red-500'> *</span>}
          </label>
        )}

        {/* 入力フィールド */}
        <div className='relative'>
          <input
            ref={ref}
            id={id}
            type='text'
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (value.trim().length >= 2 && suggestions.length > 0) {
                setIsOpen(true);
              }
            }}
            disabled={disabled}
            required={required}
            className={`w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors ${
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-700 focus:border-blue-500 focus:ring-blue-500'
            } ${disabled ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'} focus:outline-none focus:ring-1 text-gray-900 dark:text-white`}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
          />

          {/* ロード中インジケーター */}
          {isLoading && (
            <div className='absolute right-3 top-1/2 -translate-y-1/2'>
              <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-blue-500'></div>
            </div>
          )}
        </div>

        {/* エラーメッセージ */}
        {error && (
          <p id={`${id}-error`} className='mt-1 text-sm text-red-500'>
            {error}
          </p>
        )}

        {/* API取得エラー（警告表示） */}
        {suggestionsError && <p className='mt-1 text-sm text-orange-500'>{suggestionsError}</p>}

        {/* ドロップダウン */}
        {isOpen && suggestions.length > 0 && (
          <ul
            className='absolute top-full z-10 mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg'
            role='listbox'
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.placeId}
                role='option'
                aria-selected={index === selectedIndex}
                className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                  index === selectedIndex
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                }`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {/* 駅名 */}
                <div className='font-medium'>{suggestion.name}</div>

                {/* 副要素テキスト（都道府県情報など） */}
                {suggestion.secondaryText && (
                  <div className='text-xs text-gray-600 dark:text-gray-400'>
                    {suggestion.secondaryText}
                  </div>
                )}

                {/* 駅タイプ情報（該当する場合） */}
                {suggestion.types && suggestion.types.length > 0 && (
                  <div className='text-xs text-gray-500 dark:text-gray-500'>
                    {suggestion.types.join(', ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* 補完候補なし */}
        {isOpen && suggestions.length === 0 && !isLoading && value.trim().length >= 2 && (
          <div className='absolute top-full z-10 mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 shadow-lg'>
            該当する駅が見つかりません（手動入力でご利用ください）
          </div>
        )}
      </div>
    );
  }
);

StationAutocompleteInput.displayName = 'StationAutocompleteInput';
